import React, { useState, useEffect } from 'react';
import { bool, func, string, arrayOf, shape } from 'prop-types';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import classNames from 'classnames';

import { Modal, IconHeart, IconAdd, Button, IconSpinner } from '../../components';

import css from './AddToWishlistModal.module.css';

const AddToWishlistModal = props => {
  const {
    id,
    isOpen,
    onClose,
    onManageDisableScrolling,
    listingId,
    wishlists,
    wishlistIdsForListing,
    onAddToWishlist,
    onRemoveFromWishlist,
    onCreateWishlist,
    isUpdating,
  } = props;

  const intl = useIntl();
  const [selectedWishlistIds, setSelectedWishlistIds] = useState(new Set());
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newWishlistTitle, setNewWishlistTitle] = useState('');
  const [newWishlistDescription, setNewWishlistDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Initialize selected wishlists when modal opens
  useEffect(() => {
    if (isOpen && wishlistIdsForListing) {
      setSelectedWishlistIds(new Set(wishlistIdsForListing));
    }
  }, [isOpen, wishlistIdsForListing]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowCreateForm(false);
      setNewWishlistTitle('');
      setNewWishlistDescription('');
      setIsCreating(false);
    }
  }, [isOpen]);

  const handleToggleWishlist = (e, wishlistId) => {
    e.stopPropagation();
    e.preventDefault();

    const newSelected = new Set(selectedWishlistIds);
    const wasSelected = newSelected.has(wishlistId);

    if (wasSelected) {
      newSelected.delete(wishlistId);
      onRemoveFromWishlist(wishlistId, listingId);
    } else {
      newSelected.add(wishlistId);
      onAddToWishlist(wishlistId, listingId);
    }

    setSelectedWishlistIds(newSelected);
  };

  const handleCreateWishlist = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!newWishlistTitle.trim()) return;

    setIsCreating(true);
    try {
      const newWishlist = await onCreateWishlist({ title: newWishlistTitle.trim(), description: newWishlistDescription.trim() });
      // Auto-add the listing to the new wishlist
      if (newWishlist?.id) {
        await onAddToWishlist(newWishlist.id, listingId);
        setSelectedWishlistIds(prev => new Set([...prev, newWishlist.id]));
      }
      setNewWishlistTitle('');
      setNewWishlistDescription('');
      setShowCreateForm(false);
    } catch (error) {
      console.error('Failed to create wishlist:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    onClose();
  };

  const wishlistsArray = wishlists || [];
  const hasWishlists = wishlistsArray.length > 0;

  return (
    <Modal
      id={id}
      isOpen={isOpen}
      onClose={handleClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      containerClassName={css.modalContainer}
    >
      <div className={css.container} onClick={(e) => e.stopPropagation()}>
        <div className={css.header}>
          <IconHeart className={css.headerIcon} isFilled />
          <h2 className={css.title}>
            <FormattedMessage id="AddToWishlistModal.title" />
          </h2>
        </div>

        <div className={css.content}>
          {hasWishlists ? (
            <ul className={css.wishlistList}>
              {wishlistsArray.map(wishlist => {
                const isSelected = selectedWishlistIds.has(wishlist.id);
                const itemCount = wishlist.items?.length || 0;

                return (
                  <li key={wishlist.id} className={css.wishlistItem}>
                    <button
                      type="button"
                      className={classNames(css.wishlistButton, {
                        [css.wishlistButtonSelected]: isSelected,
                      })}
                      onClick={(e) => handleToggleWishlist(e, wishlist.id)}
                      disabled={isUpdating}
                    >
                      <span className={css.checkbox}>
                        {isSelected && (
                          <svg viewBox="0 0 24 24" className={css.checkIcon}>
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </span>
                      <span className={css.wishlistInfo}>
                        <span className={css.wishlistTitle}>{wishlist.title}</span>
                        <span className={css.wishlistMeta}>
                          <FormattedMessage
                            id="AddToWishlistModal.itemCount"
                            values={{ count: itemCount }}
                          />
                        </span>
                      </span>
                      {isUpdating && isSelected && (
                        <IconSpinner className={css.spinner} />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className={css.noWishlists}>
              <FormattedMessage id="AddToWishlistModal.noWishlists" />
            </p>
          )}

          {/* Create new wishlist section */}
          {showCreateForm ? (
            <form onSubmit={handleCreateWishlist} className={css.createForm} onClick={(e) => e.stopPropagation()}>
              <input
                type="text"
                className={css.createInput}
                placeholder={intl.formatMessage({ id: 'AddToWishlistModal.newWishlistPlaceholder' })}
                value={newWishlistTitle}
                onChange={(e) => setNewWishlistTitle(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                autoFocus
                disabled={isCreating}
              />
              <textarea
                className={css.createTextarea}
                placeholder={intl.formatMessage({ id: 'AddToWishlistModal.newWishlistDescriptionPlaceholder' })}
                value={newWishlistDescription}
                onChange={(e) => setNewWishlistDescription(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                disabled={isCreating}
                rows={3}
              />
              <div className={css.createFormButtons}>
                <Button
                  type="button"
                  className={css.cancelButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setShowCreateForm(false);
                    setNewWishlistTitle('');
                  }}
                  disabled={isCreating}
                >
                  <FormattedMessage id="AddToWishlistModal.cancel" />
                </Button>
                <Button
                  type="submit"
                  className={css.createSubmitButton}
                  disabled={!newWishlistTitle.trim() || isCreating}
                >
                  {isCreating ? (
                    <FormattedMessage id="AddToWishlistModal.creating" />
                  ) : (
                    <FormattedMessage id="AddToWishlistModal.create" />
                  )}
                </Button>
              </div>
            </form>
          ) : (
            <button
              type="button"
              className={css.createNewButton}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setShowCreateForm(true);
              }}
            >
              <IconAdd className={css.addIcon} />
              <FormattedMessage id="AddToWishlistModal.createNew" />
            </button>
          )}
        </div>

        <div className={css.footer}>
          <Button className={css.doneButton} onClick={handleClose}>
            <FormattedMessage id="AddToWishlistModal.done" />
          </Button>
        </div>
      </div>
    </Modal>
  );
};

AddToWishlistModal.defaultProps = {
  wishlists: [],
  wishlistIdsForListing: [],
  isUpdating: false,
};

AddToWishlistModal.propTypes = {
  id: string.isRequired,
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  listingId: string.isRequired,
  wishlists: arrayOf(
    shape({
      id: string.isRequired,
      title: string.isRequired,
    })
  ),
  wishlistIdsForListing: arrayOf(string),
  onAddToWishlist: func.isRequired,
  onRemoveFromWishlist: func.isRequired,
  onCreateWishlist: func.isRequired,
  isUpdating: bool,
};

export default AddToWishlistModal;
